import React, { useRef, useState, useCallback, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";

import "./App.css";
import ProjectLoader from "./ProjectLoader";
import { useLoaderHold, useLoaderState } from "./loaderStore";
import LeadPage, { hasSubmittedLead } from "./LeadPage";

// Lazy load all route components
const Master = lazy(() => import("./Master"));

const EarthoTour = lazy(() => import("./Eartho/Tour"));
const EarthoMap = lazy(() => import("./Eartho/Components/Map"));

// Kept dark rather than white: the landing page preloads the tour's chunk before
// navigating, so this is usually a single frame - and against the tour's black
// canvas a dark frame is invisible where a white one read as a flash.
// The route fallback doesn't draw a loader of its own - it holds the single
// app-level one (AppLoader) open while the screen's code chunk downloads.
function RouteLoader() {
  useLoaderHold(true);
  return null;
}

function AppLoader() {
  const { active, progress } = useLoaderState();
  // Keep the last percentage while fading out, rather than the bar switching
  // back to its sweep once the screen has released the loader.
  const lastProgress = useRef(null);
  if (active) lastProgress.current = progress;
  return <ProjectLoader done={!active} progress={active ? progress : lastProgress.current} />;
}

function App() {
  const mountRef = useRef(null);
  const [leadDone, setLeadDone] = useState(hasSubmittedLead);
  const navigate = useNavigate();
  // A fresh lead goes straight into the 360° tour.
  const handleLeadSuccess = useCallback(() => {
    setLeadDone(true);
    navigate("/explore", { replace: true });
  }, [navigate]);

  // The lead form is the entry point for every route; visitors who have
  // already left their details go straight through.
  if (!leadDone) {
    return (
      <div className="app-shell" style={{ overflowY: "auto" }}>
        <LeadPage onSuccess={handleLeadSuccess} />
      </div>
    );
  }

  return (
    <div
      ref={mountRef}
      className="app-container app-shell"
      style={{ overflow: "hidden" }}
    >
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          {/* Home: Explore in 360 / Master Plan */}
          <Route path="/" element={<Master />} />

          {/* The Aurora Hills */}
          <Route path="/explore" element={<EarthoTour />} />
          <Route path="/masterplan" element={<EarthoMap />} />

          {/* fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <AppLoader />
    </div>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}